/**
 * EL CICLO DEL POMODORO — pomodoros, descanso corto entre ellos y uno largo al
 * completar la tanda. Todo configurable (spec 2026-10-28, «Foco con descansos»).
 *
 * Funciones puras: la pantalla y el estado del temporizador solo las llaman. Así
 * se prueba sin reloj lo que de verdad importa —qué descanso toca y cuándo una
 * tanda deja de valer—.
 */

export type TipoDescanso = 'corto' | 'largo';
export type AjusteCiclo = TipoDescanso | 'porCiclo';

/** Límites y valores de partida: el pomodoro clásico (5, 15 y largo cada 4). */
export const AJUSTES_CICLO: Record<
  AjusteCiclo,
  { min: number; max: number; paso: number; inicial: number }
> = {
  corto: { min: 1, max: 30, paso: 1, inicial: 5 },
  largo: { min: 5, max: 60, paso: 5, inicial: 15 },
  porCiclo: { min: 2, max: 8, paso: 1, inicial: 4 },
};

/**
 * Tres horas sin pomodoros y la tanda caduca: lo de por la tarde no es la
 * continuación de lo de por la mañana, y ofrecer el descanso largo al primer
 * pomodoro del día sería raro.
 */
export const TANDA_CADUCA_MS = 3 * 60 * 60_000;

/** La misma regla que separa una sesión «cortada» (ver `foco-vigia.tsx`). */
const PARTE_QUE_CUENTA = 0.8;

export function acotaAjuste(cual: AjusteCiclo, n: number, respaldo?: number): number {
  const { min, max, inicial } = AJUSTES_CICLO[cual];
  if (!Number.isFinite(n)) return respaldo ?? inicial;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/** Si un pomodoro cuenta para el ciclo: uno cortado no da derecho a descanso. */
export function completo(plannedMinutes: number, workedMs: number): boolean {
  return workedMs >= plannedMinutes * 60_000 * PARTE_QUE_CUENTA;
}

/** Tras completar el pomodoro número `ciclo` de la tanda, qué descanso toca. */
export function descansoQueToca(ciclo: number, porCiclo: number): TipoDescanso {
  return ciclo > 0 && ciclo % porCiclo === 0 ? 'largo' : 'corto';
}

/** El ciclo que sigue valiendo ahora: cero si la tanda caducó o no hubo ninguna. */
export function cicloVigente(ciclo: number, ultimoFinMs: number | null, ahoraMs: number): number {
  if (ultimoFinMs === null) return 0;
  return ahoraMs - ultimoFinMs > TANDA_CADUCA_MS ? 0 : ciclo;
}

export type EstadoCiclo = {
  ciclo: number;
  ultimoFinMs: number | null;
  oferta: TipoDescanso | null;
};

/** Cómo queda la tanda al cerrar un pomodoro. */
export function trasPomodoro(
  antes: { ciclo: number; ultimoFinMs: number | null },
  esCompleto: boolean,
  porCiclo: number,
  ahoraMs: number,
): EstadoCiclo {
  if (!esCompleto) return { ciclo: antes.ciclo, ultimoFinMs: antes.ultimoFinMs, oferta: null };
  const ciclo = cicloVigente(antes.ciclo, antes.ultimoFinMs, ahoraMs) + 1;
  return { ciclo, ultimoFinMs: ahoraMs, oferta: descansoQueToca(ciclo, porCiclo) };
}

/** Cómo queda la tanda al cerrar un descanso: el largo la cierra. */
export function trasDescanso(
  antes: { ciclo: number },
  tipo: TipoDescanso,
  ahoraMs: number,
): { ciclo: number; ultimoFinMs: number } {
  return { ciclo: tipo === 'largo' ? 0 : antes.ciclo, ultimoFinMs: ahoraMs };
}

export function minutosDeDescanso(
  tipo: TipoDescanso,
  ajustes: { focusBreakMinutes: number; focusLongBreakMinutes: number },
): number {
  return tipo === 'largo' ? ajustes.focusLongBreakMinutes : ajustes.focusBreakMinutes;
}
